import Behavior from 'dashboard/components/GraphCanvasV2/behaviors/behavior';
import CanvasMouseDownEvent from 'dashboard/components/GraphCanvasV2/events/canvas-mouse-down-event';
import CanvasMouseMoveEvent from 'dashboard/components/GraphCanvasV2/events/canvas-mouse-move-event';
import CanvasMouseUpEvent from 'dashboard/components/GraphCanvasV2/events/canvas-mouse-up-event';
import CanvasViewportChangeEvent from 'dashboard/components/GraphCanvasV2/events/canvas-viewport-change-event';
import CanvasWheelEvent from 'dashboard/components/GraphCanvasV2/events/canvas-wheel-event';
import KeyDownEvent from 'dashboard/components/GraphCanvasV2/events/key-down-event';
import KeyUpEvent from 'dashboard/components/GraphCanvasV2/events/key-up-event';

import CanvasMouseEnterEvent from '../events/canvas-mouse-enter-event';

const ZOOM_SENSITIVITY = 1.5;
const DELTA = 0.05;
export const MIN_ZOOM = 0.02;
export const MAX_ZOOM = 2;

interface DragOriginPosition {
  x: number;
  y: number;
}

export default class CameraPanBehavior extends Behavior {
  private zPressed = false;
  private altPressed = false;
  private ctrlPressed = false;
  private metaPressed = false;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private dragOriginClientPoint: DragOriginPosition = null;

  public get events() {
    return {
      [CanvasWheelEvent.type]: 'handleWheel',
      [CanvasMouseDownEvent.type]: 'handleCanvasMouseDown',
      [CanvasMouseMoveEvent.type]: 'handleCanvasMouseMove',
      [CanvasMouseUpEvent.type]: 'handleCanvasMouseUp',
      [KeyDownEvent.type]: 'handleKeyDown',
      [KeyUpEvent.type]: 'handleKeyUp',
      [CanvasMouseEnterEvent.type]: 'handleCanvasMouseEnterEvent',
    };
  }

  public reactivate() {
    this.processSideEffects();
  }

  public reset() {
    this.zPressed = false;
    this.altPressed = false;
    this.ctrlPressed = false;
    this.metaPressed = false;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.dragOriginClientPoint = null;
  }

  public handleWheel(e: CanvasWheelEvent) {
    const canvas = this.g6Wrapper.canvas;
    // Browsers do a weird thing where they set ctrlKey to true
    // in the event this is a pinch-to-zoom rather than a pan.
    if (e.raw.ctrlKey || this.ctrlPressed || this.metaPressed) {
      const point = canvas.getPointByClient(e.raw.x, e.raw.y);
      const graphZoom = this.g6Wrapper.graph.getZoom();

      let ratio = graphZoom;
      let zoom = graphZoom;

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - incorrect typescript definition
      if (e.raw.wheelDelta < 0) {
        ratio = 1 - DELTA * ZOOM_SENSITIVITY;
      } else {
        ratio = 1 / (1 - DELTA * ZOOM_SENSITIVITY);
      }
      zoom = graphZoom * ratio;

      if (zoom > MAX_ZOOM || zoom < MIN_ZOOM) {
        return;
      }

      this.g6Wrapper.graph.zoomTo(zoom, { x: point.x, y: point.y });
      this.eventBus.dispatchEvent(new CanvasViewportChangeEvent(e, canvas));
    } else {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - incorrect typescript definition
      this.g6Wrapper.graph.translate(e.raw.wheelDeltaX, e.raw.wheelDeltaY);
      this.eventBus.dispatchEvent(new CanvasViewportChangeEvent(e, canvas));
    }
  }

  public handleCanvasMouseDown(e: CanvasMouseDownEvent) {
    this.dragOriginClientPoint = {
      x: e.raw.x,
      y: e.raw.y,
    };
  }

  public handleCanvasMouseMove(e: CanvasMouseMoveEvent) {
    if (this.dragOriginClientPoint == null) {
      return;
    }

    const dx = e.raw.x - this.dragOriginClientPoint.x;
    const dy = e.raw.y - this.dragOriginClientPoint.y;

    this.graph.translate(dx, dy);
    this.eventBus.dispatchEvent(new CanvasViewportChangeEvent(e, this.g6Wrapper.canvas));
    this.dragOriginClientPoint = {
      x: e.raw.x,
      y: e.raw.y,
    };
  }

  public handleCanvasMouseUp(e: CanvasMouseUpEvent) {
    if (this.zPressed && this.altPressed) {
      const point = this.g6Wrapper.canvas.getPointByClient(e.raw.x, e.raw.y);
      const graphZoom = this.g6Wrapper.graph.getZoom();
      const zoom = graphZoom * 0.8;

      this.g6Wrapper.graph.zoomTo(zoom, { x: point.x, y: point.y });
      return;
    } else if (this.zPressed) {
      const point = this.g6Wrapper.canvas.getPointByClient(e.raw.x, e.raw.y);
      const graphZoom = this.g6Wrapper.graph.getZoom();
      const zoom = graphZoom * 1.2;

      this.g6Wrapper.graph.zoomTo(zoom, { x: point.x, y: point.y });
      return;
    }

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.dragOriginClientPoint = null;
  }

  public handleCanvasMouseEnterEvent(_e: CanvasMouseEnterEvent) {
    this.processSideEffects();
  }

  public handleKeyDown(e: KeyDownEvent) {
    if (e.raw.code === 'KeyZ') {
      this.zPressed = true;
    } else if (e.raw.key === 'Control') {
      this.ctrlPressed = true;
    } else if (e.raw.key === 'Meta') {
      this.metaPressed = true;
    } else if (e.raw.key === 'Alt') {
      this.altPressed = true;
    }

    this.processSideEffects();
  }

  public handleKeyUp(e: KeyUpEvent) {
    if (e.raw.code === 'KeyZ') {
      this.zPressed = false;
    } else if (e.raw.key === 'Control') {
      this.ctrlPressed = false;
    } else if (e.raw.key === 'Meta') {
      this.metaPressed = false;
    } else if (e.raw.key === 'Alt') {
      this.altPressed = false;
    }

    this.processSideEffects();
  }

  // TODO (bjon): The cursor still does not update correctly upon a Viewport change (like scrolling).
  // To do that, we prob should store the x/y coords constantly in the eventbus then run the "mousemove"
  // logic within pointerEventHandler using those x/y coords
  // We use G6Wrapper.findNodesAtPosition to determine if we are hovering over a node. That function checks if
  // the cursor is within the node bbox. However, expandable nodes have an icon on the top right that protrudes
  // the bbox. When hovering over that icon, we trigger a CanvasMouseEnterEvent as we think the cursor left the node
  // and entered the canvas. That sets the cursor to select. However, since we are still on the node, the cursor
  // gets set back to default. when the cursor leaves the node, the CanvasMousEnterEvent does not refire so
  // the cursor gets stuck at default in pan mode.
  // To fix this, we most likely have to update findNodesAtPosition to conside that top right icon
  private processSideEffects() {
    const { canvasEl } = this.g6Wrapper;
    const styles = canvasEl
      .getAttribute('style')
      .split(';')
      .filter(Boolean)
      .reduce((styleHash: Record<string, unknown>, kv: string) => {
        const [key, value] = kv.split(':');
        return {
          ...styleHash,
          [key.trim()]: value.trim(),
        };
      }, {});

    let changed = false;
    if (this.zPressed && this.altPressed) {
      if (styles['cursor'] !== 'zoom-out') {
        styles['cursor'] = 'zoom-out';
        changed = true;
      }
    } else if (this.zPressed) {
      if (styles['cursor'] !== 'zoom-in') {
        styles['cursor'] = 'zoom-in';
        changed = true;
      }
    } else {
      if (styles['cursor'] !== 'grab') {
        styles['cursor'] = 'grab';
        changed = true;
      }
    }

    if (changed) {
      const styleString = Object.keys(styles).reduce((string, styleKey) => {
        return `${string}${styleKey}: ${styles[styleKey]};`;
      }, '');
      canvasEl.setAttribute('style', styleString);
    }
  }
}
